'use client';

import React from 'react';
import { useSearchParams } from 'next/navigation';
import { twJoin, twMerge } from 'tailwind-merge';
import { BundleItemDetailsFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { Bundle } from '@/ui/services/BundleService';
import { useCartStore } from '@/ui/store/cart';
import { urlParameterCodes } from '@/constants/urlParams';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { buildChooserPathForSelection, isBundlePagePath } from '@/ui/services/utils/bundle-links';
import { parseBundleSkusParam } from '@/utils/bundle-helper';
import { appendQueryStringToUrl } from '@/utils/url-helper';
import { FormatCurrency } from '@/ui/hooks/useCurrencyFormatter';
import Button from '../Button';
import { SharedButtonLinkProps } from './types';

interface BundleButtonProps extends SharedButtonLinkProps {
  bundleItem: BundleItemDetailsFragment;
  pricePosition?: string | null;
  openInNewTab?: boolean | null;
  className?: string;
  applyClassesToButton?: boolean;
  urlParamsQueryString: string;
  formatCurrency: FormatCurrency;
  currentPath: string;
}

export function BundleButton({
  bundleItem,
  pricePosition,
  openInNewTab,
  className,
  applyClassesToButton,
  urlParamsQueryString,
  formatCurrency,
  currentPath,
  text,
  children,
  variant,
  noUppercase,
  ariaLabel,
  tileElementTrackingId,
  hasExternalAreaLink,
  onClick,
  modal,
}: Readonly<BundleButtonProps>) {
  const searchParams = useSearchParams();
  const cartStore = useCartStore();
  const { country } = useLocalization();
  const bundle = new Bundle(bundleItem);
  const isOwnPage = isBundlePagePath(bundle, currentPath);
  // On its own page the page link would point back at the page, so the button opens
  // the chooser and carries the variants the visitor arrived with (overview colour dot).
  const opensChooser = !bundle.getLinkablePagePath() || isOwnPage;
  // The URL's set SKUs describe the bundle of the page the visitor is on, so another
  // bundle's CTA on that page starts from its own defaults.
  const bundleSkusParam = isOwnPage
    ? (searchParams?.get(urlParameterCodes.productDetail.bundleSkus) ?? null)
    : null;
  const arrivedWith = bundle.selectVariantsBySKUs(parseBundleSkusParam(bundleSkusParam));
  const linkPath = opensChooser
    ? buildChooserPathForSelection(bundle, arrivedWith)
    : bundle.getLinkPath();
  const href = linkPath ? appendQueryStringToUrl(linkPath, urlParamsQueryString) : undefined;

  // Without a chooser to open or a page to visit, the set goes straight into the cart
  // instead of rendering a button that does nothing.
  const addsToCart = !hasExternalAreaLink && !linkPath;
  // A set with neither a page nor a chooser has nowhere to show the unavailability notice,
  // so a selection the country does not sell disables the button instead of adding it.
  const isAddBlocked = addsToCart && bundle.isSelectionRestrictedInCountry(arrivedWith, country);
  const addSetToCart = () => {
    onClick?.();
    bundle.checkStockAndAddToCart(arrivedWith, cartStore);
  };
  const handleClick = addsToCart ? addSetToCart : onClick;

  const hasDiscount = bundle.getSaving(arrivedWith) > 0;

  const originalPrice = hasDiscount && (
    <span className="block text-sm text-neutral-50 line-through">
      {formatCurrency(bundle.getTotalOriginalPrice(arrivedWith), {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      })}
    </span>
  );

  const reducedPrice = (
    <span className="block text-md font-bold">
      {formatCurrency(bundle.getFinalPrice(arrivedWith), {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      })}
    </span>
  );

  return (
    <div className={twMerge('flex w-fit items-center gap-4', !applyClassesToButton && className)}>
      {pricePosition === 'left' && originalPrice}
      {pricePosition === 'left' && reducedPrice}
      <Button
        variant={variant}
        href={hasExternalAreaLink ? undefined : href}
        openInNewTab={hasExternalAreaLink ? undefined : openInNewTab}
        scroll={!opensChooser}
        className={twJoin(noUppercase && 'normal-case', applyClassesToButton && className)}
        onClick={isAddBlocked ? undefined : handleClick}
        disabled={isAddBlocked}
        aria-label={ariaLabel}
        data-tile-element-id={tileElementTrackingId}
      >
        {text}
        {children}
      </Button>
      {pricePosition === 'right' && reducedPrice}
      {pricePosition === 'right' && originalPrice}
      {modal}
    </div>
  );
}
